'use client'

import { useState, type CSSProperties } from 'react'
import {
  formatRubles,
  monthName,
  moscowToday,
  type MarketMoneyMonthlyPoint,
} from '@/lib/market-money'

const PLOT_HEIGHT = 228
const VISIBLE_MONTHS = 12
const EXPECTED_HATCH: CSSProperties = {
  background: 'repeating-linear-gradient(135deg, var(--color-st-delivered) 0 2px, var(--color-paper) 2px 5px)',
}

function niceCeiling(maxKopecks: number): number {
  const raw = maxKopecks / 4
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude).find((candidate) => candidate >= raw) ?? raw
  return step * 4
}

function axisLabel(kopecksValue: number): string {
  const rubles = kopecksValue / 100
  if (rubles === 0) return '0'
  if (rubles >= 1_000_000) return `${(rubles / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн`
  if (rubles >= 1_000) return `${(rubles / 1_000).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} тыс`
  return rubles.toLocaleString('ru-RU', { maximumFractionDigits: 0 })
}

function monthTitle(point: MarketMoneyMonthlyPoint): string {
  return `${monthName(point.month)} ${point.month.slice(0, 4)}`
}

export function MarketMonthlyChart({ points: allPoints, today = moscowToday(), salesLabel = 'Продажи', footnote, expectedDatesFor }: {
  points: readonly MarketMoneyMonthlyPoint[]
  today?: string
  salesLabel?: string
  footnote: string
  expectedDatesFor?: (month: string) => string | null
}) {
  const [hovered, setHovered] = useState<number | null>(null)
  const points = allPoints.slice(-VISIBLE_MONTHS)
  const maxValue = Math.max(0, ...points.map((point) => Math.max(point.salesKopecks, point.receivedKopecks + point.expectedKopecks)))
  if (points.length === 0 || maxValue === 0) {
    return <div className="flex h-[300px] items-center justify-center text-[13px] text-ink-muted">Нет данных по месяцам</div>
  }

  const ceiling = niceCeiling(maxValue)
  const ticks = [4, 3, 2, 1, 0].map((index) => (ceiling / 4) * index)
  const height = (value: number) => `${(value / ceiling) * PLOT_HEIGHT}px`
  const lastPoint = points[points.length - 1]
  const hoveredPoint = hovered === null ? null : points[hovered]
  const hoveredExpectedDates = hoveredPoint && expectedDatesFor ? expectedDatesFor(hoveredPoint.month) : null
  const columns = { gridTemplateColumns: `repeat(${points.length}, minmax(0, 1fr))` }
  const salesWord = salesLabel.toLowerCase()

  return (
    <div className="flex flex-col gap-4 px-4 py-5 sm:px-6 sm:py-[22px]">
      <div className="flex flex-wrap gap-[22px] text-[12px] text-ink-2">
        <span className="flex items-center gap-1.5"><span className="size-2.5 bg-gold" aria-hidden="true" /><span>{salesLabel}</span></span>
        <span className="flex items-center gap-1.5"><span className="size-2.5 bg-st-delivered" aria-hidden="true" /><span>Выплаты Маркета</span></span>
        <span className="flex items-center gap-1.5"><span className="size-2.5 border border-st-delivered" style={EXPECTED_HATCH} aria-hidden="true" /><span>Ожидается</span></span>
      </div>
      <div className="overflow-x-auto">
        <div className="flex min-w-[560px] gap-3">
          <div className="tab-nums flex h-[250px] w-11 flex-col justify-between pb-[22px] text-right text-[11px] text-ink-faint" aria-hidden="true">
            {ticks.map((tick) => <span key={tick} className="leading-none">{axisLabel(tick)}</span>)}
          </div>
          <div className="relative h-[250px] flex-1">
            {ticks.map((tick, index) => (
              <div key={tick} className={`absolute inset-x-0 border-t ${index === ticks.length - 1 ? 'border-rule-strong' : 'border-bone'}`} style={{ top: `${(PLOT_HEIGHT / 4) * index}px` }} />
            ))}
            <div className="absolute inset-x-0 top-0 grid items-end" style={{ ...columns, height: `${PLOT_HEIGHT}px` }}>
              {points.map((point, index) => (
                <div
                  key={point.month}
                  data-testid="market-month"
                  role="group"
                  tabIndex={0}
                  aria-label={`${monthTitle(point)}: ${salesWord} ${formatRubles(point.salesKopecks)}, выплаты Маркета ${formatRubles(point.receivedKopecks)}, ожидается ${formatRubles(point.expectedKopecks)}`}
                  onMouseEnter={() => setHovered(index)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(index)}
                  onBlur={() => setHovered(null)}
                  className={`flex h-full items-end justify-center gap-1.5 outline-none ${hovered === index ? 'bg-gold/8' : ''}`}
                >
                  <div className="flex w-[30px] flex-col justify-end">
                    <div className="rounded-t-[3px] bg-gold" style={{ height: height(point.salesKopecks) }} />
                  </div>
                  <div className="flex w-[22px] flex-col justify-end gap-0.5">
                    {point.expectedKopecks > 0 && <div className="rounded-t-[3px] border border-st-delivered" style={{ ...EXPECTED_HATCH, height: height(point.expectedKopecks) }} />}
                    {point.receivedKopecks > 0 && <div className="bg-st-delivered" style={{ height: height(point.receivedKopecks) }} />}
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute inset-x-0 bottom-0 grid text-center text-[12px] text-ink-muted" style={columns} aria-hidden="true">
              {points.map((point, index) => (
                <span key={point.month} className={hovered === index ? 'font-semibold text-ink' : ''}>{monthName(point.month)}</span>
              ))}
            </div>
            {hoveredPoint && hovered !== null && (
              <div
                role="tooltip"
                className="pointer-events-none absolute top-1 z-10 flex w-[200px] flex-col gap-px rounded-[4px] bg-ink px-2.5 py-2 text-[11px] leading-normal text-ivory"
                style={{
                  left: `${((hovered + 0.5) / points.length) * 100}%`,
                  transform: `translateX(${hovered === 0 ? '-20%' : hovered === points.length - 1 ? '-80%' : '-50%'})`,
                }}
              >
                <span className="font-semibold text-gold-soft">{monthTitle(hoveredPoint)}</span>
                <span className="tab-nums">{salesLabel} {formatRubles(hoveredPoint.salesKopecks)}</span>
                <span className="tab-nums">Выплаты Маркета {formatRubles(hoveredPoint.receivedKopecks)}</span>
                {hoveredPoint.expectedKopecks > 0 && (
                  <span className="tab-nums">
                    Ожидается {formatRubles(hoveredPoint.expectedKopecks)}
                    {hoveredExpectedDates ? ` · ${hoveredExpectedDates}` : ''}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1 border-t border-rule pt-3.5 text-[13px] text-ink-muted sm:flex-row sm:gap-2.5">
        {lastPoint.month === today.slice(0, 7) && <span>{monthName(lastPoint.month)} ещё идёт, выплаты за него ожидаются.</span>}
        <span>{footnote}</span>
      </div>
    </div>
  )
}
